/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import type { UserPreferences } from '@poweralpha/common';
import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import { agentConversationListQueryKey } from './api';

type KeepRecentFor = UserPreferences['agent']['keepRecentFor'];
type ListPage = {
  items: { conversation_id: string; updated_at?: string | null; created_at?: string | null }[];
};

export const ACTIVE_AT_STORAGE_KEY = 'agent-conversation-active-at';
const HOUR_MS = 60 * 60 * 1000;

/** Notes that the user just used the agent (the panel closed), per browser tab. */
export const markAgentActivity = () =>
  sessionStorage.setItem(ACTIVE_AT_STORAGE_KEY, new Date().toISOString());

/**
 * When the conversation last saw activity: its `updated_at` from the cached history list, or
 * the last time this tab's panel closed, whichever is later.
 */
export const getLastAgentActivity = (queryClient: QueryClient, conversationId: string) => {
  const times: number[] = [];
  for (const [, data] of queryClient.getQueriesData<InfiniteData<ListPage>>({
    queryKey: agentConversationListQueryKey,
  })) {
    for (const page of data?.pages ?? []) {
      const item = page.items?.find((entry) => entry.conversation_id === conversationId);
      const stamp = item?.updated_at ?? item?.created_at;
      if (stamp) times.push(Date.parse(stamp));
    }
  }
  const activeAt = sessionStorage.getItem(ACTIVE_AT_STORAGE_KEY);
  if (activeAt) times.push(Date.parse(activeAt));
  const valid = times.filter(Number.isFinite);
  return valid.length ? new Date(Math.max(...valid)) : null;
};

/** Settings › Agent › “Keep the last one if it’s recent”. */
export const isRecentActivity = (
  lastActivity: Date | null,
  keepRecentFor: KeepRecentFor,
  now = new Date(),
) => {
  if (!lastActivity || keepRecentFor === 'never') return false;
  if (keepRecentFor === '1h') return now.getTime() - lastActivity.getTime() <= HOUR_MS;
  return lastActivity.toDateString() === now.toDateString();
};
